<template>
  <div>
    <!-- Blood Compatibility -->
    <b-modal
      id="modal-edit-blood-compatibility"
      ok-only
      centered
      size="lg"
      :title="`${$t('common.edit')} ${$t('office.blood-compatibility')}`"
    >
      <template #modal-footer="{}">
        <b-button
          variant="primary"
          @click="submitBloodCompatibility"
          class="m-0"
          :disabled="loading"
          ref="button"
          block
        >
          <span v-if="!loading">{{ $t("common.submit") }}</span>
          <b-spinner small v-else></b-spinner>
        </b-button>
      </template>
      <b-row>
        <b-col md="12" cols="12">
          <b-form-group
            :label="$t('office.source-of-blood')"
            label-for="source_of_blood"
          >
            <b-form-input
              v-model="editBloodCompatibilityForm.source_of_blood"
            />
          </b-form-group>
        </b-col>
        <b-col md="6" cols="12">
          <b-form-group
            :label="$t('office.transfusion-start-time')"
            label-for="transfusion_start_time"
          >
            <datetime
              input-class="form-control"
              type="datetime"
              v-model="editBloodCompatibilityForm.transfusion_start_time"
            />
          </b-form-group>
        </b-col>
        <b-col md="6" cols="12">
          <b-form-group
            :label="$t('office.time-transfusion-completed')"
            label-for="time_transfusion_completed"
          >
            <datetime
              input-class="form-control"
              type="datetime"
              v-model="editBloodCompatibilityForm.time_transfusion_completed"
            />
          </b-form-group>
        </b-col>
        <b-col md="6" cols="12">
          <b-form-group
            :label="$t('office.pre-transfusion-hb')"
            label-for="pre_transfusion_hb"
          >
            <b-form-input
              type="number"
              v-model="editBloodCompatibilityForm.pre_transfusion_hb"
            />
          </b-form-group>
        </b-col>
        <b-col md="6" cols="12">
          <b-form-group
            :label="$t('office.post-transfusion-hb')"
            label-for="post_transfusion_hb"
          >
            <b-form-input
              type="number"
              v-model="editBloodCompatibilityForm.post_transfusion_hb"
            />
          </b-form-group>
        </b-col>
        <b-col md="12" cols="12">
          <b-form-group
            :label="$t('office.completion-of-transfusion-note')"
            label-for="completion_of_transfusion_note"
          >
            <b-form-textarea
              v-model="
                editBloodCompatibilityForm.completion_of_transfusion_note
              "
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-modal>

    <!-- Blood Transfusion Reactions -->
    <b-modal
      id="modal-edit-blood-transfusion-reactions"
      ok-only
      centered
      size="lg"
      :title="$t('common.edit') + ' Blood Transfusion Reactions'"
    >
      <template #modal-footer="{}">
        <b-button
          variant="primary"
          @click="submitBloodTransfusionReactions"
          class="m-0"
          :disabled="loading"
          ref="button"
          block
        >
          <span v-if="!loading">{{ $t("common.submit") }}</span>
          <b-spinner small v-else></b-spinner>
        </b-button>
      </template>
      <b-row>
        <b-col
          md="6"
          cols="12"
          v-for="(field, index) in bloodTransfusionReactionsFields"
          :key="index"
        >
          <b-form-group :label="field.label" :label-for="field.model">
            <b-form-input
              v-if="field.type === 'text'"
              v-model="editBloodTransfusionReactionsForm[field.model]"
            />
            <b-form-input
              v-else-if="field.type === 'number'"
              type="number"
              v-model="editBloodTransfusionReactionsForm[field.model]"
            />
            <b-form-select
              v-else-if="field.type === 'select'"
              v-model="editBloodTransfusionReactionsForm[field.model]"
            >
              <b-form-select-option :value="1">Yes</b-form-select-option>
              <b-form-select-option :value="0">No</b-form-select-option>
            </b-form-select>
            <!-- Add more input types as needed -->
          </b-form-group>
        </b-col>
      </b-row>
    </b-modal>

    <!-- Vital Signs -->
    <b-modal
      :no-close-on-backdrop="true"
      id="modal-edit"
      ok-only
      centered
      size="lg"
      :title="
        createMode
          ? `${$t('common.add')} ${$t('office.vital-sign')}`
          : `${$t('common.edit')} ${$t('office.vital-sign')}`
      "
    >
      <template #modal-footer="{}">
        <b-button
          variant="primary"
          @click="VitalSignsSubmit"
          class="m-0"
          :disabled="loading"
          ref="button"
          block
        >
          <span v-if="!loading">{{ $t("common.submit") }}</span>
          <b-spinner small v-else></b-spinner>
        </b-button>
      </template>
      <b-card-text>
        <b-row>
          <b-col md="6" cols="12">
            <b-form-group
              :label="$t('office.vital-signs-types')"
              label-for="status"
            >
              <AutoComplete
                v-model="EditForm.vital_sign_type"
                placeholder="Select vital sign type"
                :serializer="(val) => val"
                endpoint="vital-sign-types"
                text-title="name"
                @input="setVitalSignTypeValue"
              />
              <div
                class="d-flex justify-content-between"
                v-if="vital_sign_type_value.max || vital_sign_type_value.min"
              >
                <span
                  >({{ $t("common.minValue") }} -
                  {{ vital_sign_type_value.min }})</span
                >
                <span>
                  ({{ $t("common.maxValue") }} -
                  {{ vital_sign_type_value.max }})</span
                >
              </div>
            </b-form-group>
          </b-col>
          <b-col md="6" cols="12">
            <b-form-group
              :label="$t('office.minutes-after-transfusion')"
              label-for="status"
            >
              <v-select
                v-model="EditForm.minutes_after_transfusion"
                :options="minutes_after_transfusionOptions"
                label="text"
                :serializer="(val) => val.value"
                placeholder="Select minutes after transfusion"
              />
            </b-form-group>
          </b-col>

          <b-col md="6" cols="12">
            <b-form-group :label="$t('common.value')" label-for="value">
              <b-form-input
                v-model="EditForm.value"
                :placeholder="$t('common.value')"
              />
            </b-form-group>
          </b-col>
          <b-col md="6" cols="12">
            <b-form-group :label="$t('common.date')" label-for="date">
              <datetime
                input-class="form-control"
                type="datetime"
                v-model="date"
              >
              </datetime>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
    </b-modal>

    <b-row>
      <b-col style="cursor: pointer" @click="$router.go(-1)" md="12" cols="12">
        <statistic-card-horizontal
          icon=""
          :statistic="$t('common.go-back')"
          class="modify"
        />
      </b-col>
      <b-col cols="12" sm="12">
        <!-- Patient Info -->
        <b-card>
          <h2
            class="text-center"
            style="font-weight: bold; text-transform: capitalize"
          >
            patient info
          </h2>
          <hr />
          <div>
            <h5 class="my-2">
              <b>{{ $t("common.name") }}:</b>
              <p class="mx-2 d-inline d-inline">
                {{ patientCardData.patient.name }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.companion-name") }}:</b>
              <p class="mx-2 d-inline d-inline">
                {{ patientCardData.full_companion_name }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.companion-phone") }}:</b>
              <p class="mx-2 d-inline">{{ patientCardData.companion_phone }}</p>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.next-kin-name") }}:</b>
              <p class="mx-2 d-inline d-inline">
                {{ patientCardData.next_of_kin_name }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.next-kin-phone") }}:</b>
              <p class="mx-2 d-inline d-inline">
                {{ patientCardData.next_of_kin_phone_number }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.card-type") }}:</b>
              <p class="mx-2 d-inline">{{ patientCardData.type }}</p>
            </h5>
            <h5 class="my-2">
              <b class="text-danger">{{ $t("reception.allergy") }}:</b>
              <p class="mx-2 d-inline text-danger">
                {{ patientCardData.allergy }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.smoking") }}:</b>
              <p class="mx-2 d-inline">
                {{ !patientCardData.is_smoker ? "No" : "Yes" }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.diabetic") }}:</b>
              <p class="mx-2 d-inline">
                {{ !patientCardData.has_diabetes ? "No" : "Yes" }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.heart-disease") }}:</b>
              <p class="mx-2 d-inline">
                {{ !patientCardData.has_heart_disease ? "No" : "Yes" }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.hypertension") }}:</b>
              <p class="mx-2 d-inline">
                {{ !patientCardData.has_high_blood_pressure ? "No" : "Yes" }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.weight") }}:</b>
              <p class="mx-2 d-inline">{{ patientCardData.weight }}</p>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.height") }}:</b>
              <p class="mx-2 d-inline">{{ patientCardData.height }}</p>
            </h5>

            <h5 class="my-2">
              <b>{{ $t("reception.room") }}:</b>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.patient-case") }}:</b>
              <p class="mx-2 d-inline">{{ patientCardData.patient_case }}</p>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.diagnosis") }}:</b>
              <p class="mx-2 d-inline">{{ patientCardData.diagnosis }}</p>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.investigation") }}:</b>
              <p class="mx-2 d-inline">{{ patientCardData.investigation }}</p>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.treatment") }}:</b>
              <p class="mx-2 d-inline">{{ patientCardData.treatment }}</p>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.examination") }}:</b>
              <p class="mx-2 d-inline">{{ patientCardData.examination }}</p>
            </h5>
            <!-- <h5 class="my-2">
                <b>{{ $t('reception.patient-case-detalis') }}:</b>
                <p class="mx-2 d-inline">
                  {{ patientCardData.patient_case_details }}
                </p>
              </h5> -->
          </div>

          <hr class="invoice-spacing" />
          <h2 class="my-2">{{ $t("reception.patient-updates") }}</h2>
          <div>
            <h5 class="my-2">
              <b>{{ $t("reception.date-of-last-update") }}:</b>
              <p class="mx-2 d-inline">{{ patientCardData.updated_at }}</p>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.patient-info-created-at") }}:</b>
              <p class="mx-2 d-inline">{{ patientCardData.created_at }}</p>
            </h5>
            <h5 class="my-2">
              <b>{{ $t("reception.card-no") }}:</b>
              <p class="mx-2 d-inline">{{ patientCardData.card_no }}</p>
            </h5>
          </div>

          <b-row>
            <b-col cols="12" md="3"></b-col>
            <b-col cols="12" md="6">
              <!-- <DoctorNotesBox
                  v-if="
                    patientCardData.type == 'IN' ||
                    patientCardData.type == 'EMERGENCY' ||
                    patientCardData.type == 'CLINIC' ||
                    patientCardData.type == 'OUT'
                  "
                /> -->
            </b-col>
            <b-col cols="12" md="3"></b-col>
          </b-row>
        </b-card>
        <!-- Blood Compatibility -->
        <b-card>
          <h2
            class="text-center"
            style="font-weight: bold; text-transform: capitalize"
          >
            Blood Compatibility
          </h2>
          <hr />
          <div style="display: flex; justify-content: flex-end">
            <b-button
              variant="primary"
              @click="showBloodCompatibility(bloodCompatibilityData)"
            >
              {{ $t("common.edit") }}
            </b-button>
          </div>
          <div>
            <h5 class="my-2">
              <b>source of blood:</b>
              <p class="mx-2 d-inline">
                {{ bloodCompatibilityData.source_of_blood }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>transfusion start time:</b>
              <p class="mx-2 d-inline">
                {{ bloodCompatibilityData.transfusion_start_time }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>time transfusion completed:</b>
              <p class="mx-2 d-inline">
                {{ bloodCompatibilityData.time_transfusion_completed }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>pre transfusion hb:</b>
              <p class="mx-2 d-inline">
                {{ bloodCompatibilityData.pre_transfusion_hb }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>post transfusion hb:</b>
              <p class="mx-2 d-inline">
                {{ bloodCompatibilityData.post_transfusion_hb }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>completion of transfusion note:</b>
              <p class="mx-2 d-inline">
                {{ bloodCompatibilityData.completion_of_transfusion_note }}
              </p>
            </h5>
          </div>
        </b-card>

        <!-- Blood Transfusion Reactions -->
        <b-card>
          <h2
            class="text-center"
            style="font-weight: bold; text-transform: capitalize"
          >
            Blood Transfusion Reactions
          </h2>
          <hr />
          <div style="display: flex; justify-content: flex-end">
            <b-button
              variant="primary"
              @click="
                showBloodTransfusionReactions(bloodTransfusionReactionsData)
              "
            >
              {{ $t("common.edit") }}
            </b-button>
          </div>
          <div v-if="">
            <h5 class="my-2">
              <b>minutes duration unitl adverse reaction:</b>
              <p class="mx-2 d-inline">
                {{
                  bloodTransfusionReactionsData.minutes_duration_unitl_adverse_reaction
                }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>type of reaction:</b>
              <p class="mx-2 d-inline">
                {{ bloodTransfusionReactionsData.type_of_reaction }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>rigor reaction:</b>
              <p class="mx-2 d-inline">
                {{
                  bloodTransfusionReactionsData.rigor_reaction === 1
                    ? "Yes"
                    : "No"
                }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>blood pressure:</b>
              <p class="mx-2 d-inline">
                {{ bloodTransfusionReactionsData.blood_pressure }}
              </p>
            </h5>
            <!-- <h5 class="my-2">
              <b>nausea:</b>
              <p class="mx-2 d-inline">
                {{ bloodTransfusionReactionsData.nausea === 1 ? "Yes" : "No" }}
              </p>
            </h5> -->
            <h5 class="my-2">
              <b>vomiting:</b>
              <p class="mx-2 d-inline">
                {{
                  bloodTransfusionReactionsData.vomiting === 1 ? "Yes" : "No"
                }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>pain_at:</b>
              <p class="mx-2 d-inline">
                {{ bloodTransfusionReactionsData.pain_at }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>urine_color:</b>
              <p class="mx-2 d-inline">
                {{ bloodTransfusionReactionsData.urine_color }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>urine_output_decrease:</b>
              <p class="mx-2 d-inline">
                {{ bloodTransfusionReactionsData.urine_output_decrease }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>fever_temperature:</b>
              <p class="mx-2 d-inline">
                {{ bloodTransfusionReactionsData.fever_temperature }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>tachycardia:</b>
              <p class="mx-2 d-inline">
                {{ bloodTransfusionReactionsData.tachycardia }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>dyspuea:</b>
              <p class="mx-2 d-inline">
                {{ bloodTransfusionReactionsData.dyspuea }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>others:</b>
              <p class="mx-2 d-inline">
                {{ bloodTransfusionReactionsData.others }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>management of treatment:</b>
              <p class="mx-2 d-inline">
                {{ bloodTransfusionReactionsData.management_of_treatment }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>cause of reaction:</b>
              <p class="mx-2 d-inline">
                {{ bloodTransfusionReactionsData.cause_of_reaction }}
              </p>
            </h5>
            <h5 class="my-2">
              <b>treatment:</b>
              <p class="mx-2 d-inline">
                {{ bloodTransfusionReactionsData.treatment }}
              </p>
            </h5>
          </div>
        </b-card>

        <!-- Vital Signs -->
        <b-card>
          <h2
            class="text-center"
            style="font-weight: bold; text-transform: capitalize"
          >
            vital signs
          </h2>
          <hr />
          <div>
            <DeleteModal
              class="display-none-print"
              modalId="modal-delete"
              @deleteToggle="deleteItem"
            />

            <b-card :title="$t('office.vital-signs')">
              <div>
                <b-row class="display-none-print">
                  <b-col cols="12" md="12">
                    <h2>
                      {{ $t("common.filter") }} {{ $t("office.vital-signs") }}
                    </h2>
                    <br />
                  </b-col>

                  <b-col md="4" cols="12">
                    <b-form-group
                      :label="$t('office.vital-signs-types')"
                      label-for="Name"
                    >
                      <AutoComplete
                        v-model="typeFilter"
                        placeholder="Select vital sign type"
                        :serializer="(val) => val.name"
                        endpoint="vital-sign-types"
                        text-title="name"
                        @input="setVitalSignTypeValue"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="4" cols="12">
                    <b-form-group :label="$t('common.date')" label-for="Name">
                      <datepicker
                        v-model="dateFilter"
                        :calendar-button="true"
                        :clear-button="true"
                        :bootstrap-styling="true"
                        class="datebtn2"
                        placeholder="YYYY-MM-DD"
                      ></datepicker>
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Search Button -->
                <b-row class="display-none-print">
                  <b-col cols="12" md="12">
                    <br />
                  </b-col>
                  <b-col
                    cols="12"
                    md="12"
                    class="d-flex align-items-center justify-content-end mb-1 mb-md-0"
                  >
                    <b-button variant="primary" @click="getVitalSigns()">
                      {{ $t("common.search") }}
                      <b-icon-search class="ml-1"></b-icon-search>
                    </b-button>
                  </b-col>
                </b-row>
                <!-- Data Table  -->
                <div
                  class="d-flex justify-content-between mt-4 mb-2 display-none-print"
                >
                  <h2 class="display-none-print">
                    {{ $t("common.table") }} {{ $t("office.vital-signs") }} :{{
                      $route.params.name
                    }}
                  </h2>

                  <div class="d-flex">
                    <b-button
                      v-if="$store.state.app.permissions.includes('store-test')"
                      variant="primary"
                      class="display-none-print mx-1"
                      @click="show(EditForm, 'create')"
                    >
                      {{ $t("common.create") }} {{ $t("office.vital-sign") }}
                    </b-button>
                    <PrintVitalSignVue
                      :refresh="refresh"
                      :url="fetchUrl"
                      :data="printData"
                    />
                  </div>
                </div>
                <b-table
                  :items="data"
                  responsive
                  :fields="tableColumns"
                  primary-key="id"
                  show-empty
                  empty-text="No matching results found"
                  class="position-relative display-none-print"
                  v-if="!loading"
                >
                  <!-- Column: Actions -->
                  <template #cell(value)="data">
                    <span
                      class="px-1"
                      v-if="data.item.result_as_to_normal_range.is_normal"
                      >{{ data.item.value }}</span
                    >
                    <span class="px-1" v-else style="color: red">{{
                      data.item.value
                    }}</span>
                    <!-- Check if the message contains "Below" -->
                    <feather-icon
                      v-if="
                        data.item.result_as_to_normal_range.message.includes(
                          'Below'
                        )
                      "
                      icon="ArrowDownIcon"
                      size="18"
                    />

                    <!-- Check if the message contains "Above" -->
                    <feather-icon
                      v-else-if="
                        data.item.result_as_to_normal_range.message.includes(
                          'Above'
                        )
                      "
                      icon="ArrowUpIcon"
                      size="18"
                    />
                    <span class="px-1">{{
                      data.item.result_as_to_normal_range.message
                    }}</span>
                  </template>

                  <template #cell(normal_value)="data">
                    <span
                      >{{ data.item.vital_sign_type_data.min_normal_value }} -
                      {{
                        data.item.vital_sign_type_data.max_normal_value
                      }}</span
                    >
                  </template>
                  <template #cell(minutes_after_transfusion)="data">
                    {{
                      replaceMinutesForVitalSigns(
                        data.item.minutes_after_transfusion
                      )
                    }}
                  </template>
                  <template #cell(actions)="data">
                    <ActionsButtons
                      deletePerm="delete-patient-doctor-note"
                      editPerm="update-patient-doctor-note"
                      @editToggle="show(data.item)"
                      @deleteToggle="deleteModel(data.item)"
                    />
                  </template>
                </b-table>

                <template v-if="loading">
                  <div>
                    <div class="d-flex justify-content-center mb-1">
                      <b-spinner :label="$t('common.loading')" />
                    </div>
                  </div>
                </template>
                <div class="mx-2 mb-2 display-none-print">
                  <b-row>
                    <b-col
                      cols="12"
                      sm="6"
                      class="d-flex align-items-center justify-content-center justify-content-sm-start"
                    >
                      <span class="text-muted"
                        >{{ $t("common.showing") }} 1 {{ $t("common.to") }} 10
                        {{ $t("reception.doctor-notes") }}</span
                      >
                    </b-col>
                    <!-- Pagination -->
                    <b-col
                      cols="12"
                      sm="6"
                      class="d-flex align-items-center justify-content-center justify-content-sm-end"
                      @click="getVitalSigns()"
                    >
                      <b-pagination
                        v-model="page"
                        :total-rows="dataCount"
                        :per-page="take"
                        first-number
                        last-number
                        class="mb-0 mt-1 mt-sm-0"
                        prev-class="prev-item"
                        next-class="next-item"
                      >
                        <template #prev-text>
                          <feather-icon icon="ChevronLeftIcon" size="18" />
                        </template>
                      </b-pagination>
                    </b-col>
                  </b-row>
                </div>
              </div>
            </b-card>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>
<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BLink,
  BCardText,
  BFormGroup,
  BFormTextarea,
  BFormInput,
  BSpinner,
  BFormCheckbox,
  BTable,
  BDropdown,
  BDropdownItem,
  BPagination,
  BIcon,
  BFormSelect,
  BFormSelectOption,
} from "bootstrap-vue";
import StatisticCardHorizontal from "@core/components/statistics-cards/StatisticCardHorizontal.vue";
const AutoComplete = () => import("@/components/common/AutoComplete.vue");
import vSelect from "vue-select";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ActionsButtons from "@/components/common/ActionsButtons.vue";
import DeleteModal from "@/components/modals/DeleteModal.vue";
import { Datetime } from "vue-datetime";
import Datepicker from "vuejs-datepicker";
import VueHtml2pdf from "vue-html2pdf";
import "vue-datetime/dist/vue-datetime.css";
import axios from "axios";
import qs from "qs";

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BFormInput,
    BFormTextarea,
    BFormGroup,
    BSpinner,
    BCard,
    BCardText,
    BButton,
    BFormCheckbox,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    BFormTextarea,
    BIcon,
    BFormSelect,
    BFormSelectOption,
    StatisticCardHorizontal,
    AutoComplete,
    vSelect,
    ToastificationContent,
    ActionsButtons,
    DeleteModal,
    Datetime,
    Datepicker,
    VueHtml2pdf,
  },
  data() {
    return {
      loading: false,
      params: {
        form: {},
        path: "",
      },
      patientCardData: {},
      dataCount: 0,
      tableColumns: [
        {
          key: "vital_sign_type",
          label: this.$t("office.vital_sign_type"),
          sortable: true,
        },
        {
          key: "minutes_after_transfusion",
          label: this.$t("office.minutes_after_transfusion"),
          sortable: true,
        },
        {
          key: "normal_value",
          label: this.$t("office.normal_value"),
          sortable: true,
        },
        { key: "value", label: this.$t("common.value"), sortable: true },
        { key: "date", label: this.$t("common.date"), sortable: true },

        { key: "Actions", label: this.$t("common.actions") },
      ],
      take: 100,
      isNotValid: false,
      pationtData: {},
      data: [],
      date: new Date(Date.now()).toISOString(),
      EditForm: {
        minutes_after_transfusion: null,
        blood_compatibility_id: this.$route.params.blood_compatibility_id,
        date: "",
        value: "",
        vital_sign_type: "",
        patient_card_id: this.$route.params.pateint_card_id,
        _method: "PUT",
      },
      vital_sign_type_value: {
        min: 0,
        max: 0,
      },
      createMode: false,
      errorForm: {
        date: "",
        value: "",
        vital_sign_type: "",
        patient_card_id: "",
        minutes_after_transfusion: null,
        blood_compatibility_id: null,
      },
      page: 1,
      fetchUrl: "",
      printData: [],
      refresh: false,
      typeFilter: null,
      dateFilter: null,
      blood_compatibility_id: this.$route.params.blood_compatibility_id,
      pateint_card_id: this.$route.params.pateint_card_id,
      minutes_after_transfusionOptions: [
        { text: "Pre-transfusion", value: -1 },
        { text: "15 minutes", value: 15 },
        { text: "30 minutes", value: 30 },
        { text: "1 hour", value: 60 },
        { text: "2 hours", value: 120 },
        { text: "3 hours", value: 180 },
        { text: "4 hours", value: 240 },
        // { text: "5 hours", value: 300 },
        // { text: "6 hours", value: 360 },
        // { text: "7 hours", value: 420 },
        // { text: "8 hours", value: 480 },
        // { text: "9 hours", value: 540 },
        // { text: "10 hours", value: 600 },
      ],
      editBloodTransfusionReactionsForm: {
        blood_compatibility_id: this.$route.params.blood_compatibility_id,
        minutes_duration_unitl_adverse_reaction: "",
        type_of_reaction: "",
        rigor_reaction: "",
        blood_pressure: "",
        nausea: "",
        vomiting: "",
        pain_at: "",
        urine_color: "",
        urine_output_decrease: "",
        fever_temperature: "",
        tachycardia: "",
        dyspuea: "",
        others: "",
        management_of_treatment: "",
      },
      bloodTransfusionReactionsData: [],
      bloodTransfusionReactionsFields: [
        {
          label: "Minutes Duration Until Adverse Reaction",
          model: "minutes_duration_unitl_adverse_reaction",
          type: "number",
        },
        { label: "Type of Reaction", model: "type_of_reaction", type: "text" },
        { label: "Rigor Reaction", model: "rigor_reaction", type: "select" },
        { label: "Blood Pressure", model: "blood_pressure", type: "text" },
        // { label: "Nausea", model: "nausea", type: "select" },
        { label: "Vomiting", model: "vomiting", type: "select" },
        { label: "Pain At", model: "pain_at", type: "text" },
        { label: "Urine Color", model: "urine_color", type: "text" },
        {
          label: "Urine Output Decrease",
          model: "urine_output_decrease",
          type: "text",
        },
        {
          label: "Fever Temperature",
          model: "fever_temperature",
          type: "number",
        },
        { label: "Tachycardia", model: "tachycardia", type: "number" },
        { label: "Dyspuea", model: "dyspuea", type: "number" },
        { label: "Others", model: "others", type: "text" },
        {
          label: "Management of Treatment",
          model: "management_of_treatment",
          type: "text",
        },
        {
          label: "Cause of Reaction",
          model: "cause_of_reaction",
          type: "text",
        },
        {
          label: "Treatment",
          model: "treatment",
          type: "text",
        },
      ],
      bloodCompatibilityData: {},
      editBloodCompatibilityForm: {
        source_of_blood: "",
        transfusion_start_time: "",
        time_transfusion_completed: "",
        pre_transfusion_hb: "",
        post_transfusion_hb: "",
        completion_of_transfusion_note: "",
      },
    };
  },
  mounted() {
    this.getPatientCard();
    this.getVitalSigns();
    this.getBloodCompatibility();
    this.getBloodTransfusionReactions();
  },

  methods: {
    replaceMinutesForVitalSigns(minutes) {
      if (minutes === -1) {
        return "Pre-transfusion";
      }
      if (minutes === 15) {
        return "15 minutes";
      }
      if (minutes === 30) {
        return "30 minutes";
      }
      if (minutes === 60) {
        return "1 hour";
      }
      if (minutes === 120) {
        return "2 hours";
      }
      if (minutes === 180) {
        return "3 hours";
      }
      if (minutes === 240) {
        return "4 hours";
      }
      return minutes + " minutes";
    },
    setVitalSignTypeValue(value) {
      this.vital_sign_type_value = {
        min: value.min_normal_value,
        max: value.max_normal_value,
      };
    },
    formatDate(dateString) {
      const date = new Date(dateString);

      if (date) {
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${year}-${month}-${day} ${hours}:${minutes}:00`;
    },
    formatDateWithoutTime(unformatedDate) {
      const date = new Date(unformatedDate);

      const year = date.getFullYear();
      let month = "" + (date.getMonth() + 1);
      let day = "" + date.getDate();

      if (month.length < 2) month = "0" + month;
      if (day.length < 2) day = "0" + day;

      // Return the formatted date string
      return `${year}-${month}-${day}`;
    },
    convertToISO(dateString) {
      // Create a new Date object from the input date string
      const date = new Date(dateString.replace(" ", "T") + "Z");

      // Return the ISO string representation of the date
      return date.toISOString();
    },
    async getPatientCard() {
      this.loading = true;
      await this.getList(`patientCard/${this.$route.params.pateint_card_id}`);
      this.loading = false;

      this.patientCardData = this.all_data;
      console.log("this.patientCardData", this.patientCardData);
    },
    show(item, mode) {
      if (mode === "create") {
        this.createMode = true;
        this.EditForm = {
          date: "",
          value: "",
          vital_sign_type: "",
          patient_card_id: this.$route.params.pateint_card_id,
          minutes_after_transfusion: null,
          blood_compatibility_id: this.$route.params.blood_compatibility_id,
          _method: "POST",
        };
      } else {
        this.createMode = false;
        this.EditForm.id = item.id;
        this.EditForm.value = item.value;
        this.EditForm.date = this.formatDate(item.date);
        this.EditForm.vital_sign_type = item.vital_sign_type;
        this.EditForm.patient_card_id = item.patient_card_id;
        this.EditForm.minutes_after_transfusion =
          item.minutes_after_transfusion;
        this.EditForm.blood_compatibility_id = item.blood_compatibility_id;
        this.EditForm._method = "PUT";
      }
      this.$bvModal.show("modal-edit");
    },
    async getVitalSigns() {
      this.loading = true;
      // check if there are any null value in the obj we sending

      let url = `/vital-signs?filter[patient_card_id]=${this.$route.params.pateint_card_id}&include=patientCard,vitalSignTypeData&page=${this.page}&take=${this.take}&sort=-id`;

      if (this.typeFilter) {
        url += `&filter[vital_sign_type]=${this.typeFilter}`;
      }
      if (this.dateFilter) {
        url += `&filter[date]=${this.formatDateWithoutTime(this.dateFilter)}`;
      }
      this.fetchUrl = url;
      await this.getList(url);
      this.dataCount = this.all_data.total;
      this.data = this.all_data.data;
      this.printData = this.data;
      // this.prepareChartData(this.data);
      this.loading = false;
      this.refresh = !this.refresh;
    },

    deleteModel(item) {
      this.deleteForm = item;
      this.$bvModal.show("modal-delete");
    },
    deleteItem() {
      this.$store
        .dispatch("delete", `/vital-signs/${this.deleteForm.id}`)
        .then((response) => {
          this.getVitalSigns();

          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Delete Success",
              icon: "success",
              variant: "info",
              text: "deleted " + this.deleteForm.first_name + " successfully",
            },
          });
        })
        .catch((error) => {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              variant: "danger",
              text: "Error deleting " + this.deleteForm.first_name,
            },
          });
        });
    },
    VitalSignsSubmit() {
      this.loading = true;
      for (let item in this.errorForm) {
        this.errorForm[item] = "";
        if (this.EditForm[item] == "" || this.EditForm[item] == null)
          delete this.EditForm[item];
      }
      this.EditForm.date = this.formatDate(this.date);
      this.EditForm.minutes_after_transfusion =
        this.EditForm.minutes_after_transfusion.value;
      if (this.createMode) {
        this.EditForm.vital_sign_type = this.EditForm.vital_sign_type.name;
        console.log("this.EditForm", this.EditForm);

        (this.params.form = this.EditForm),
          (this.params.path = `/vital-signs`),
          this.$store
            .dispatch("create", this.params)
            .then((response) => {
              this.getVitalSigns();
              this.$bvModal.hide("modal-edit");
              this.setVitalSignTypeValue({
                min_normal_value: 0,
                max_normal_value: 0,
              });
              this.loading = false;
            })
            .catch((error) => {
              this.loading = false;
              if (error.response.status === 400) {
                for (let item in error.response.data) {
                  this.$toast({
                    component: ToastificationContent,
                    position: "top-right",
                    props: {
                      title: "validation error",
                      variant: "danger",
                      text: error.response.data[item][0],
                    },
                  });
                }
              }
            });
      } else {
        (this.params.form = this.EditForm),
          (this.params.path = `/vital-signs/${this.EditForm.id}?_method=PUT`),
          this.$store
            .dispatch("update", this.params)
            .then((response) => {
              this.getVitalSigns();
              this.$bvModal.hide("modal-edit");
              this.setVitalSignTypeValue({
                min_normal_value: 0,
                max_normal_value: 0,
              });
              this.loading = false;
            })
            .catch((error) => {
              this.loading = false;
              if (error.response.status === 400) {
                for (let item in error.response.data) {
                  this.$toast({
                    component: ToastificationContent,
                    position: "top-right",
                    props: {
                      title: "validation error",
                      variant: "danger",
                      text: error.response.data[item][0],
                    },
                  });
                }
              }
            });
      }
    },

    /////////////// blood compatibility ////////////

    async getBloodCompatibility() {
      this.loading = true;
      await this.getList(
        `bloodCompatibilities/${this.$route.params.blood_compatibility_id}`
      );
      this.loading = false;
      this.bloodCompatibilityData = this.all_data;
      console.log("this.bloodCompatibilityData", this.bloodCompatibilityData);
    },
    showBloodCompatibility(item) {
      console.log("date", this.formatDate(this.date));
      console.log("item.source_of_blood", item);
      if (item.source_of_blood) {
        this.editBloodCompatibilityForm = {
          source_of_blood: item.source_of_blood,
          transfusion_start_time: this.convertToISO(
            item.transfusion_start_time
          ),
          time_transfusion_completed: this.convertToISO(
            item.time_transfusion_completed
          ),
          pre_transfusion_hb: item.pre_transfusion_hb,
          post_transfusion_hb: item.post_transfusion_hb,
          completion_of_transfusion_note: item.completion_of_transfusion_note,
        };
      }
      console.log(
        "this.editBloodCompatibilityForm",
        this.editBloodCompatibilityForm
      );
      this.$bvModal.show("modal-edit-blood-compatibility");
    },
    async submitBloodCompatibility() {
      this.loading = true;
      this.editBloodCompatibilityForm.transfusion_start_time = this.formatDate(
        this.editBloodCompatibilityForm.transfusion_start_time
      );
      this.editBloodCompatibilityForm.time_transfusion_completed =
        this.formatDate(
          this.editBloodCompatibilityForm.time_transfusion_completed
        );
      console.log(
        "this.editBloodCompatibilityForm",
        this.editBloodCompatibilityForm
      );
      const data = qs.stringify(this.editBloodCompatibilityForm);
      await axios
        .put(
          `bloodCompatibilities/${this.$route.params.blood_compatibility_id}`,
          data,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        )
        .then((response) => {
          this.getBloodCompatibility();
          this.$bvModal.hide("modal-edit-blood-compatibility");
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          console.log("error", error);
        });
    },

    /////////////// blood transfusion reactions ////////////
    async getBloodTransfusionReactions() {
      this.loading = true;
      await this.getList(
        `bloodTransfusionReactions?filter[blood_compatibility_id]=${this.$route.params.blood_compatibility_id}&sort[id]=-id`
      );
      this.bloodTransfusionReactionsData = this.all_data.data[0] || [];
      this.loading = false;
      console.log(
        "this.bloodTransfusionReactionsData",
        this.bloodTransfusionReactionsData
      );
    },
    showBloodTransfusionReactions(item) {
      if (item) {
        this.editBloodTransfusionReactionsForm = {
          blood_compatibility_id: this.$route.params.blood_compatibility_id,
          minutes_duration_unitl_adverse_reaction:
            item.minutes_duration_unitl_adverse_reaction,
          type_of_reaction: item.type_of_reaction,
          rigor_reaction: item.rigor_reaction,
          blood_pressure: item.blood_pressure,
          nausea: item.nausea,
          vomiting: item.vomiting,
          pain_at: item.pain_at,
          urine_color: item.urine_color,
          urine_output_decrease: item.urine_output_decrease,
          fever_temperature: item.fever_temperature,
          tachycardia: item.tachycardia,
          dyspuea: item.dyspuea,
          others: item.others,
          management_of_treatment: item.management_of_treatment,
        };
        console.log(
          "this.editBloodTransfusionReactionsForm",
          this.editBloodTransfusionReactionsForm
        );
      }
      this.$bvModal.show("modal-edit-blood-transfusion-reactions");
    },
    async submitBloodTransfusionReactions() {
      this.loading = true;

      for (let item in this.editBloodTransfusionReactionsForm) {
        if (
          this.editBloodTransfusionReactionsForm[item] == "" ||
          this.editBloodTransfusionReactionsForm[item] == null
        )
          delete this.editBloodTransfusionReactionsForm[item];
      }

      const data = this.editBloodTransfusionReactionsForm;
      data.rigor_reaction === 1
        ? (data.rigor_reaction = 1)
        : (data.rigor_reaction = 0);
      data.nausea === 1 ? (data.nausea = 1) : (data.nausea = 0);
      data.vomiting === 1 ? (data.vomiting = 1) : (data.vomiting = 0);

      console.log("data submit Reaction", data);

      // Define the text fields to check
      const textFields = [
        "type_of_reaction",
        "blood_pressure",
        "pain_at",
        "urine_color",
        "urine_output_decrease",
        "others",
        "management_of_treatment",
        "cause_of_reaction",
        "treatment",
      ];

      // Check if all text fields exist in editBloodTransfusionReactionsForm
      const areAllTextFieldsPresent = textFields.some(
        (field) => field in this.editBloodTransfusionReactionsForm
      );

      if (!areAllTextFieldsPresent) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Validation Error",
            variant: "danger",
            text: "Some required fields are missing: " + textFields.filter(field => !(field in this.editBloodTransfusionReactionsForm)).join(", "),
          },
        });
        this.loading = false;
        return;
      }

      (this.params.form = data),
        (this.params.path = `/bloodTransfusionReactions`),
        this.$store
          .dispatch("create", this.params)
          .then((response) => {
            this.loading = false;
            this.getBloodTransfusionReactions();
            this.$bvModal.hide("modal-edit-blood-transfusion-reactions");
          })
          .catch((error) => {
            if (error.response.status === 400) {
              for (let item in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "validation error",
                    variant: "danger",
                    text: error.response.data[item][0],
                  },
                });
              }
            }
          });
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.mb-md-0 {
  margin-top: 8px;
}

.start-box {
  display: flex;
  flex-direction: column;
  align-items: start;
  margin: 0 1rem;
  position: absolute;
  right: 1%;
  top: -13.5%;
}

table {
  width: 94%;
  border: 1px solid;
  font-size: 11px;
}

.no-border {
  border: 0;
}

.square-dark {
  background-color: rgba(0, 0, 0, 0.2);
}

tr,
th,
td {
  border: 1px solid;
  width: 10px;
  height: 10px;
  text-align: center;
}

.font-modify {
  letter-spacing: normal !important;
}

.pdf-table {
  page-break-inside: avoid;
}

.square {
  width: 30px;
  /* Smaller width for squares */
  height: 30px;
  /* Smaller height for squares */
  background-color: #000;
  /* Color of the square */
  display: inline-block;
  /* Allows us to set width and height */
}

.centered-text {
  text-align: center;
  border: 2px solid #000;
  /* Border for the cell */
}

#table-above {
  border-collapse: collapse;
  /* Removes space between borders */
  font-size: 12px;
  /* Smaller font size for the entire table */
}

@media print {
  @page {
  }

  .pdf-table {
    page-break-before: avoid;
    page-break-inside: avoid;
  }
}

.pdf-table:first-child {
  margin-top: 1em;
  /* Adjust the value as needed */
  page-break-inside: avoid;
}
.font-modify {
  letter-spacing: normal !important;
}
</style>
